import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import AROverlays from "@/components/AROverlays";
import { Switch } from "@/components/ui/switch";
import { buildFilterCSS } from "@/data/cinema";
import { DEMO_CAMERA } from "@/constants/images";
import { useApp } from "@/context/AppContext";
import { useLang, useCinema } from "@/i18n/LanguageContext";
import { toast } from "@/components/ui/sonner";

const TOGGLE_KEYS = ["grid", "golden", "horizon", "frame", "subject", "light", "motion", "depth", "text"];

export default function ARCamera() {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const { t, lang } = useLang();
  const { AR_GUIDES, FILTER_BY_ID, TEMPLATE_BY_ID } = useCinema();
  const { activeFilterId, activeTemplateId, setSharedImage, setActiveTemplateId } = useApp();
  const [live, setLive] = useState(false);
  const [guideIdx, setGuideIdx] = useState(0);
  const [toggles, setToggles] = useState({ grid: true, golden: false, horizon: true, frame: true, subject: true, light: true, motion: true, depth: false, text: true });

  const filter = FILTER_BY_ID[activeFilterId];
  const template = TEMPLATE_BY_ID[activeTemplateId];
  const filterCSS = filter ? buildFilterCSS({ ...filter.base, intensity: 70 }) : "none";
  const rtl = lang === "ar";

  useEffect(() => {
    let stream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        if (videoRef.current) { videoRef.current.srcObject = stream; setLive(true); }
      } catch { setLive(false); }
    })();
    return () => stream?.getTracks().forEach((tr) => tr.stop());
  }, []);

  useEffect(() => {
    const id = setInterval(() => setGuideIdx((i) => (i + 1) % AR_GUIDES.length), 2800);
    return () => clearInterval(id);
  }, [AR_GUIDES.length]);

  const capture = () => {
    const canvas = canvasRef.current;
    const w = 1280, h = 720;
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.filter = filterCSS !== "none" ? filterCSS : "none";
    try {
      if (live && videoRef.current?.videoWidth) {
        ctx.drawImage(videoRef.current, 0, 0, w, h);
        const data = canvas.toDataURL("image/jpeg", 0.85);
        setSharedImage(data); toast.success(t("t_captured")); return data;
      }
    } catch { /* tainted */ }
    setSharedImage(DEMO_CAMERA); toast.success(t("t_capturedDemo")); return DEMO_CAMERA;
  };

  const analyze = () => { capture(); toast.info(t("t_analyzingShot")); navigate("/app/analysis"); };

  return (
    <div className="relative" dir={rtl ? "rtl" : "ltr"} data-testid="ar-camera-page">
      <div className="relative w-full overflow-hidden" style={{ height: "68vh", background: "#04060d" }}>
        <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" style={{ filter: filterCSS, display: live ? "block" : "none" }} />
        {!live && <img src={DEMO_CAMERA} alt="demo" className="absolute inset-0 w-full h-full object-cover" style={{ filter: filterCSS }} />}
        <canvas ref={canvasRef} className="hidden" />

        <div className="absolute top-3 right-3 z-20 flex items-center gap-2 bg-black/50 rounded-full px-3 py-1.5 backdrop-blur">
          <span className={`w-2 h-2 rounded-full ${live ? "bg-red-500 animate-pulse" : "bg-gold"}`} />
          <span className="text-[11px] text-slate-200">{live ? t("liveCamera") : t("demoPreview")}</span>
        </div>

        {(filter || template) && (
          <div className="absolute top-3 left-3 z-20 bg-black/50 rounded-full px-3 py-1.5 backdrop-blur flex items-center gap-2">
            <i className="fa-solid fa-wand-magic-sparkles text-gold text-xs" />
            <span className="text-[11px] text-gold">{template ? template.title.split("–")[0].trim() : filter.name}</span>
            <button data-testid="clear-filter" onClick={() => { setActiveTemplateId(null); toast(t("t_removedTemplate")); }} className="text-slate-400"><i className="fa-solid fa-xmark text-xs" /></button>
          </div>
        )}

        <AROverlays t={toggles} />

        {toggles.text && (
          <AnimatePresence mode="wait">
            <motion.div key={guideIdx} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 max-w-[90%]">
              <div className="glass glass-blue rounded-full px-5 py-2.5 flex items-center gap-2" data-testid="ar-guidance">
                <i className="fa-solid fa-circle-info text-ai" />
                <span className="text-sm text-slate-100">{AR_GUIDES[guideIdx]}</span>
              </div>
            </motion.div>
          </AnimatePresence>
        )}

        {template && toggles.text && (
          <div className="absolute top-14 left-3 z-20 glass rounded-xl px-3 py-2 max-w-[55%]">
            <p className="text-[10px] text-gold mb-1">{t("templateInstructions")}</p>
            <p className="text-[11px] text-slate-200 leading-relaxed">{template.shot} • {template.angle} • {template.lighting}</p>
          </div>
        )}
      </div>

      <div className="px-4 -mt-8 relative z-30 flex items-center justify-center gap-4">
        <button data-testid="analyze-shot-btn" onClick={analyze} className="glass glass-blue rounded-2xl px-5 py-3 text-sm text-ai flex items-center gap-2">
          <i className="fa-solid fa-magnifying-glass-chart" /> {t("analyzeShot")}
        </button>
        <button data-testid="capture-btn" onClick={capture} className="w-16 h-16 rounded-full btn-gold flex items-center justify-center shrink-0">
          <i className="fa-solid fa-camera text-2xl" />
        </button>
        <button data-testid="ar-to-filters" onClick={() => navigate("/app/filters")} className="glass gold-border rounded-2xl px-5 py-3 text-sm text-gold flex items-center gap-2">
          <i className="fa-solid fa-sliders" /> {t("nav_filters")}
        </button>
      </div>

      <div className="px-4 mt-6">
        <h3 className="font-display text-slate-200 mb-3 flex items-center gap-2"><i className="fa-solid fa-toggle-on text-gold" /> {t("arGuides")}</h3>
        <div className="glass rounded-3xl p-4 grid grid-cols-1 gap-1">
          {TOGGLE_KEYS.map((key) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
              <span className="text-sm text-slate-300">{t(`tg_${key}`)}</span>
              <Switch data-testid={`toggle-${key}`} checked={toggles[key]} onCheckedChange={(v) => setToggles((s) => ({ ...s, [key]: v }))} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
